import { create } from 'zustand';
import type { ApiError } from '@/shared/lib/problem-details';
import { errorStatus, printingErrorCode, PRINTING_ERRORS, SUBSCRIPTION_REQUIRED } from './printing-errors';
import { formatTime } from './printing-format';
import type {
  AgentRelease,
  AgentReleaseAgent,
  AgentUpdateRequest,
  AgentUpdateRequestResponse,
} from '../types/printing.types';

/* ==========================================================================
   Atualização do computador da impressão (change print-agent)

   A API é a autoridade: calcula o nível pelo canal da loja a cada leitura e
   guarda o pedido. O painel só decide o que mostrar a quem pode pedir
   (`settings.write`): o aviso da recomendada, o diálogo da obrigatória e o
   estado de cada computador na lista da aba Impressão.
   ========================================================================== */

/** O pedido ainda vai acontecer ou está acontecendo: nada a pedir de novo. */
export function isOpenUpdateRequest(request: AgentUpdateRequest | null | undefined): boolean {
  return request?.status === 'requested' || request?.status === 'in_progress';
}

/** O último pedido terminou sem a versão nova: falhou ou venceu sem o computador aplicar. */
export function lastUpdateAttemptFailed(agent: AgentReleaseAgent): boolean {
  const status = agent.updateRequest?.status;
  return status === 'failed' || status === 'expired';
}

/** O nível pede atenção e não há pedido aberto. */
export function needsUpdateRequest(agent: AgentReleaseAgent): boolean {
  return agent.level !== 'none' && !isOpenUpdateRequest(agent.updateRequest);
}

export type AgentUpdateTone = 'success' | 'warning' | 'danger';

export interface AgentUpdateState {
  text: string;
  tone: AgentUpdateTone;
}

/**
 * O estado da atualização na linha do computador. O pedido aberto vem primeiro: com ele,
 * o que interessa é quando o computador atualiza, e não o nível que o levou a pedir.
 */
export function agentUpdateState(agent: AgentReleaseAgent): AgentUpdateState {
  const request = agent.updateRequest;
  if (request && isOpenUpdateRequest(request)) {
    if (request.phase === 'waiting_for_empty_queue') return { text: 'Aguardando a fila esvaziar', tone: 'warning' };
    if (request.status === 'in_progress' || request.phase) return { text: 'Atualizando…', tone: 'warning' };
    return { text: `Agendada para ${formatTime(request.notBefore)}`, tone: 'warning' };
  }
  if (!agent.protocolSupported) return { text: 'Impressão parada: precisa atualizar', tone: 'danger' };
  if (agent.level !== 'none' && lastUpdateAttemptFailed(agent)) {
    return { text: 'A última atualização falhou', tone: 'danger' };
  }
  if (agent.level === 'mandatory') return { text: 'Atualização obrigatória', tone: 'danger' };
  if (agent.level === 'recommended') return { text: 'Atualização disponível', tone: 'warning' };
  return { text: 'Atualizado', tone: 'success' };
}

/** O que a resposta de sucesso diz ao dono. */
export function describeUpdateRequested(response: AgentUpdateRequestResponse): string {
  const { request } = response;
  if (request.mode === 'end_of_day') {
    return `Atualização agendada para as ${formatTime(request.notBefore)}. A impressão continua funcionando até lá.`;
  }
  return 'Atualização pedida: o computador atualiza assim que não houver cupom na fila.';
}

/** Mensagem das falhas que mantêm o aviso ou o diálogo aberto. */
export const UPDATE_REQUEST_FAILED =
  'Não foi possível pedir a atualização agora. Confira a internet e tente de novo.';

/**
 * Como a tela reage à recusa do pedido de atualização:
 * - `settled`: a API diz que não há mais o que pedir (já em andamento, ou não precisa).
 *   Aviso e diálogo fecham para este computador nesta aba, e o estado é relido;
 * - `refused`: a impressão deixou de estar disponível para a loja (assinatura, piloto,
 *   desligamento geral). O estado é relido, e a releitura esconde aviso e diálogo;
 * - `retry`: rede, 5xx ou corrida perdida no servidor. Continua aberto, com a mensagem.
 */
export type UpdateRequestErrorKind = 'settled' | 'refused' | 'retry';

export function classifyUpdateRequestError(error: unknown): UpdateRequestErrorKind {
  const code = printingErrorCode(error);
  if (code === PRINTING_ERRORS.agentUpdateInProgress || code === PRINTING_ERRORS.agentUpdateNotNeeded) {
    return 'settled';
  }
  if (
    code === PRINTING_ERRORS.accessBlocked ||
    code === SUBSCRIPTION_REQUIRED ||
    code === PRINTING_ERRORS.disabled ||
    code === PRINTING_ERRORS.platformDisabled
  ) {
    return 'refused';
  }
  const status = errorStatus(error) ?? 0;
  if (status === 0 || status >= 500 || code === PRINTING_ERRORS.requestConflict) return 'retry';
  // Outra recusa (o computador revogado no meio do caminho, por exemplo): a releitura
  // decide, sem mensagem de erro que o dono não tem o que fazer.
  return 'refused';
}

/** A leitura foi recusada pela API (403 ou 404): aviso e diálogo não aparecem. */
export function isRefusedRead(error: ApiError | null | undefined): boolean {
  const status = errorStatus(error);
  return status === 403 || status === 404;
}

/* ==========================================================================
   O que fica só na memória da aba

   "Depois" vale até a próxima abertura do painel (carregamento do app): nada
   vai para a API nem para o armazenamento do navegador. O mesmo para o
   computador cuja atualização a API disse que já está em andamento ou não é
   mais necessária. A chave é loja + computador + nível: a troca de loja não
   esconde o aviso da outra, e o "Depois" do aviso recomendado fecha só o
   aviso — se o mesmo computador passa a obrigatório na mesma aba (a API
   recusou o protocolo dele, por exemplo), o diálogo aparece.
   ========================================================================== */

interface AgentUpdatePromptState {
  closed: Record<string, true>;
  close: (key: string) => void;
  reset: () => void;
}

export const useAgentUpdatePromptStore = create<AgentUpdatePromptState>((set) => ({
  closed: {},
  close: (key) => set((state) => ({ closed: { ...state.closed, [key]: true } })),
  reset: () => set({ closed: {} }),
}));

export function promptKey(tenantId: string, agentId: string, level: UpdatePrompt['level']): string {
  return `${tenantId}:${agentId}:${level}`;
}

/** Para os testes: uma aba nova. */
export function resetAgentUpdatePrompt(): void {
  useAgentUpdatePromptStore.getState().reset();
}

export interface UpdatePrompt {
  agent: AgentReleaseAgent;
  level: 'recommended' | 'mandatory';
}

/**
 * O que o `PanelLayout` mostra: o diálogo obrigatório vence o aviso. Computador com
 * pedido aberto, ou fechado nesta aba no nível em que está agora, fica de fora.
 */
export function pickUpdatePrompt(
  release: AgentRelease,
  isClosed: (agentId: string, level: UpdatePrompt['level']) => boolean,
): UpdatePrompt | null {
  const candidates = release.agents.filter(
    (agent) => needsUpdateRequest(agent) && agent.level !== 'none' && !isClosed(agent.agentId, agent.level),
  );
  const mandatory = candidates.find((agent) => agent.level === 'mandatory');
  if (mandatory) return { agent: mandatory, level: 'mandatory' };
  const recommended = candidates.find((agent) => agent.level === 'recommended');
  return recommended ? { agent: recommended, level: 'recommended' } : null;
}
